import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["onDragenter", "onDragover", "onDragleave", "onDrop"]
const _hoisted_2 = {
  key: 1,
  class: "file-list d-flex flex-column align-items-center px-3 pt-3 text-left"
}
const _hoisted_3 = {
  key: 0,
  class: "text-nowrap ml-1"
}
const _hoisted_4 = { class: "d-none d-md-block" }
const _hoisted_5 = { class: "mb-3" }
const _hoisted_6 = {
  key: 1,
  class: "mb-3"
}
const _hoisted_7 = {
  key: 2,
  class: "small"
}
const _hoisted_8 = {
  key: 3,
  class: "small"
}
const _hoisted_9 = ["id", "name", "disabled", "accept", "multiple"]

import { computed, ref, Ref, watch } from 'vue'
import { decimalFormat } from '@grantstreet/psc-js/utils/numbers.js'
import Alert from '../Alert.vue'
import LoadingBars from '@grantstreet/loaders-vue/LoadingBars.vue'
import TruncateString from '../TruncateString.vue'
import { fileTypeMap } from './supported-file-types.ts'
import type { FilePickerProps } from './component-props.ts'
import FileDetails from './FileDetails.ts'


export default /*#__PURE__*/_defineComponent({
  __name: 'FilePicker',
  props: {
    id: {},
    name: {},
    disabled: { type: Boolean },
    fileTypes: {},
    maxFileSize: {},
    multiple: { type: Boolean },
    processFile: { type: Function }
  },
  emits: ["update:modelValue", "update:isProcessing"],
  setup(__props: any, { emit }) {

const props = __props;

/**
 * This is a form input that allows users to select files from their machine.
 * This can be used to, e.g., include files in a form submission or upload them
 * somewhere.
 *
 * This supports single or multiple files and can enforce file types and max
 * individual file sizes. Also support drag and drop.
 */





/**
 * How many "process file" actions are currently in progress? The user can spawn
 * multiple such actions (each of which could contain multiple files)
 * simultaneously by re-opening the file picker while a file is already being
 * processed. We want to show the loading indicator until the very last one is
 * complete.
 */
const isProcessing = ref(0)

const dragActive = ref(false)
const fileInput: Ref<HTMLInputElement | null> = ref(null)
const fileErrors: Ref<string[]> = ref([])

/**
 * A list of FileDetails for each file "added" in the widget. This uses custom
 * FileDetails objects instead of the standard File class so that we can
 * re-inflate the widget value with a list of files later. For example, if when
 * these files are uploaded to a server and the user wants to edit their form
 * submission, we won't have access to the original File objects but only
 * metadata about the files. But we'll still want to show the files in the
 * picker and resubmit the file metadata with the form.
 */
const fileList: Ref<FileDetails[]> = ref([])

// Emit the new input value (a list of file objects) when the file list changes
watch(fileList, newFiles => emit('update:modelValue', newFiles))

// Emit whether or not any file is currently being processed
watch(isProcessing, newValue => emit('update:isProcessing', Boolean(newValue)))

/**
 * Should the widget allow the user to add another file? If `multiple` is false
 * and they have already added a file, this will be false.
 */
const canAddMore = computed(() => {
  return !props.disabled && (
    props.multiple || (
      !fileList.value.length &&
      !isProcessing.value
    )
  )
})

/**
 * Validates that the passed file type is supported by the fileTypeMap. Uses a
 * TypeScript assertion so that after this is called the type can be safely used
 * as a key lookup in fileTypeMap.
 */
function validateFileType (
  type: string,
): asserts type is keyof typeof fileTypeMap {
  if (!Object.hasOwn(fileTypeMap, type)) {
    throw new Error(`Unknown file type '${type}' passed to FilePicker.vue: see fileTypeMap for supported types`)
  }
}

/**
 * A flat array of accepted file types (file extensions or MIME types)
 */
const allAcceptedFileTypes = computed(() => {
  return props.fileTypes?.flatMap((type: string) => {
    validateFileType(type)
    return fileTypeMap[type].types
  })
})

const fileTypesAcceptProp = computed(() => {
  return allAcceptedFileTypes.value?.join(',')
})

const fileTypesDescription = computed(() => {
  return props.fileTypes?.map((type: string) => {
    validateFileType(type)
    return fileTypeMap[type].description
  }).join(', ')
})

/**
 * Parses the maxFileSize prop and returns the max number of bytes for a single
 * file (if `multiple` is true, the list of files could add up to more than the
 * individual max)
 */
const maxSizeBytes = computed(() => {
  if (!props.maxFileSize) {
    return
  }

  const maxSize = String(props.maxFileSize).replace(/\s/g, '').toUpperCase()

  if (!/^\d+(\.\d+)?(KB|MB)?$/.test(maxSize)) {
    throw new Error(`Invalid maxSize value '${maxSize}': does not match format like '5', '5KB', '5.1 MB'`)
  }

  if (maxSize.includes('KB')) {
    return Number(maxSize.replace('KB', '')) * 1024
  }
  else if (maxSize.includes('MB')) {
    return Number(maxSize.replace('MB', '')) * 1024 * 1024
  }
  else {
    return Number(maxSize)
  }
})

/**
 * Returns a user-friendly description of the max file size (e.g., "1.5 MB")
 */
function prettyFileSize (number: number) {
  const kb = 1024
  const mb = 1024 * 1024
  if (number < kb) {
    return `${number} bytes`
  }
  else if (number < mb) {
    return `${decimalFormat(number / kb, true)} KB`
  }
  else {
    return `${decimalFormat(number / mb, true)} MB`
  }
}

function setDragActive () {
  dragActive.value = true
}

function setDragInactive () {
  dragActive.value = false
}

function openFilePicker () {
  if (!canAddMore.value) {
    return
  }

  fileInput.value?.click()
}

function onDrop (event: DragEvent) {
  setDragInactive()

  if (!event.dataTransfer) {
    return
  }

  handleFiles(event.dataTransfer.files)
}

function handleInputChange (event: Event) {
  const target = event.target as HTMLInputElement
  if (!target.files) {
    return
  }
  handleFiles(target.files)

  // Clear the file input value. Otherwise if the user re-selects the same file
  // a second time (e.g., because they got an error the first time) the @change
  // handler won't be fired again.
  target.value = ''
}

/**
 * Validates the files and calls props.processFile for each file
 */
async function handleFiles (originalFiles: FileList) {
  if (!canAddMore.value) {
    return
  }

  clearErrors()

  if (!originalFiles.length) {
    return
  }

  // If the user dropped multiple files into the widget but `multiple` is false,
  // only accept the first file
  const files = props.multiple ? [ ...originalFiles] : [ originalFiles[0] ]

  for (const file of files) {
    if (maxSizeBytes.value && file.size > maxSizeBytes.value) {
      fileErrors.value.push(`File '${file.name}' (${prettyFileSize(file.size)}) is larger than the maximum allowed size of ${prettyFileSize(maxSizeBytes.value)}.`)
    }
    if (!fileIsAcceptedType(file)) {
      fileErrors.value.push(`File '${file.name}' is not a supported file type. Allowed file types: '${fileTypesDescription.value}'.`)
    }
  }

  if (fileErrors.value.length) {
    return
  }

  if (!files.length) {
    return
  }

  await Promise.all([...files].map(async file => {
    isProcessing.value++

    const fileDetails = new FileDetails({
      file,
      name: file.name,
      sizeBytes: file.size,
      mimeType: file.type,
    })

    try {
      await props.processFile?.(fileDetails)

      fileList.value.push(fileDetails)
    }
    catch (error) {
      fileErrors.value.push((error as Error).toString())
    }

    isProcessing.value--
  }))
}

/**
 * Does the passed file have an accepted file extension? This naively checks the
 * file extension/MIME type of the File object, which will not catch cases where
 * a user renames a bad file to have an accepted extension. Make sure to also do
 * back-end validation of file types.
 *
 * Returns true if no fileTypes were passed to this component.
 */
function fileIsAcceptedType (file: File) {
  const fileExtension = file.name.match(/(?:\.([^.]+))?$/)?.[0]
  return !allAcceptedFileTypes.value?.length ||
    checkFileType(file.type) ||
    (
      fileExtension &&
      allAcceptedFileTypes.value.includes(fileExtension)
    )
}

/**
 * Checks if the requested MIME type matches any of the accepted MIME types.
 * Supports wildcard accepted MIME types (e.g. image/*).
 *
 * Returns true if no accepted file types were passed to this component.
 */
function checkFileType (inputType: string) {
  if (!allAcceptedFileTypes.value) {
    return true
  }

  for (const acceptedType of allAcceptedFileTypes.value) {
    // check for exact match
    if (acceptedType === inputType) {
      return true
    }

    // check for wildcard match
    const acceptedTypeParts = acceptedType.split('/')
    if (acceptedTypeParts.length === 2 && acceptedTypeParts[1] === '*') {
      const wildcard = acceptedTypeParts[0]
      const inputTypeCategory = inputType.split('/')?.[0]
      if (wildcard === inputTypeCategory) {
        return true
      }
    }
  }
  return false
}

function clearErrors () {
  fileErrors.value = []
}

function removeFile (fileDetails: FileDetails) {
  clearErrors()

  const index = fileList.value.indexOf(fileDetails)
  if (index !== -1) {
    fileList.value.splice(index, 1)
  }
}

return (_ctx: any,_cache: any) => {
  const _component_svgicon = _resolveComponent("svgicon")!
  const _component_b_link = _resolveComponent("b-link")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["file-picker d-flex flex-column", { active: dragActive.value && canAddMore.value }]),
    onDragenter: _withModifiers(setDragActive, ["prevent"]),
    onDragover: _withModifiers(setDragActive, ["prevent"]),
    onDragleave: _withModifiers(setDragInactive, ["prevent"]),
    onDrop: _withModifiers(onDrop, ["prevent"])
  }, [
    (fileErrors.value.length)
      ? (_openBlock(), _createBlock(Alert, {
          key: 0,
          variant: "danger",
          class: "text-left mx-3 mt-3 mb-0",
          onDismissed: _cache[0] || (_cache[0] = ($event: any) => (fileErrors.value = []))
        }, {
          default: _withCtx(() => [
            _createElementVNode("ul", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fileErrors.value, (error) => {
                return (_openBlock(), _createElementBlock("li", { key: error }, _toDisplayString(error), 1))
              }), 128))
            ])
          ], undefined, true),
          _: 1
        }))
      : _createCommentVNode("", true),
    (fileList.value.length || isProcessing.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("ul", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fileList.value, (fileDetails) => {
              return (_openBlock(), _createElementBlock("li", {
                key: fileDetails.name + fileDetails.sizeBytes
              }, [
                _createVNode(_component_svgicon, { icon: "document" }),
                _createElementVNode("span", null, [
                  _createTextVNode(" File: "),
                  _createVNode(TruncateString, {
                    class: "font-weight-bold",
                    string: fileDetails.name
                  }, null, 8, ["string"]),
                  (fileDetails.sizeBytes)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(prettyFileSize(fileDetails.sizeBytes)), 1))
                    : _createCommentVNode("", true)
                ]),
                _createVNode(_component_b_link, {
                  disabled: props.disabled,
                  onClick: ($event: any) => (removeFile(fileDetails))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(" Remove ")
                  ], undefined, true),
                  _: 2
                }, 1032, ["disabled", "onClick"])
              ]))
            }), 128))
          ]),
          (isProcessing.value)
            ? (_openBlock(), _createBlock(LoadingBars, {
                key: 0,
                class: "mt-1",
                small: "",
                "include-text": false
              }))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass(["p-3", {
        'cursor-pointer': canAddMore.value
      }]),
      onClick: openFilePicker
    }, [
      (!fileList.value.length && !isProcessing.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createVNode(_component_svgicon, {
              icon: "upload",
              class: "mb-2 mx-auto"
            }),
            _createElementVNode("div", _hoisted_4, " Drop " + _toDisplayString(props.multiple ? 'one or more files' : 'a file') + " here or ", 1),
            _createElementVNode("div", _hoisted_5, [
              _createVNode(_component_b_link, {
                class: "file-link font-weight-bold",
                disabled: !canAddMore.value,
                onClick: _withModifiers(openFilePicker, ["stop"])
              }, {
                default: _withCtx(() => [
                  _createTextVNode("Browse files")
                ], undefined, true),
                _: 1
              }, 8, ["disabled", "onClick"])
            ])
          ], 64))
        : (props.multiple)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createVNode(_component_b_link, {
                class: "file-link font-weight-bold",
                disabled: !canAddMore.value,
                onClick: _withModifiers(openFilePicker, ["stop"])
              }, {
                default: _withCtx(() => [
                  _createTextVNode("Add more files")
                ], undefined, true),
                _: 1
              }, 8, ["disabled", "onClick"])
            ]))
          : _createCommentVNode("", true),
      (fileTypesDescription.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createTextVNode(" File formats accepted: "),
            _createElementVNode("strong", null, _toDisplayString(fileTypesDescription.value), 1)
          ]))
        : _createCommentVNode("", true),
      (maxSizeBytes.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createTextVNode(" File size limit: "),
            _createElementVNode("strong", null, _toDisplayString(prettyFileSize(maxSizeBytes.value)), 1)
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("input", {
        id: props.id,
        ref_key: "fileInput",
        ref: fileInput,
        name: props.name,
        disabled: !canAddMore.value,
        class: "d-none",
        type: "file",
        accept: fileTypesAcceptProp.value,
        multiple: props.multiple,
        onChange: handleInputChange
      }, null, 40, _hoisted_9)
    ], 2)
  ], 42, _hoisted_1))
}
}

})